// Pure draft, diff, assertion, and Locator helpers for transcript labeling.
// React and network work stay in LabelInspector; every resolved range is
// constructed through the marker-authoring Locator builder so the two
// authoring surfaces share validation and half-open semantics.

import type { components } from "../../api/schema";
import {
  blankLocator,
  buildLocator,
  type Assertion,
  type Locator,
  type MarkerOutput,
} from "../markerAuthoringDraft";

type AnswerResponse = components["schemas"]["AnswerResponse"];
type LabelCreate = components["schemas"]["LabelCreate"];
type FlaggedReason = "ambiguous" | "low_quality" | "off_topic";

// The `F` shortcut cycles the flag through this order.
export const FLAG_CYCLE: readonly (FlaggedReason | null)[] = [
  null,
  "ambiguous",
  "low_quality",
  "off_topic",
];

export function nextFlaggedReason(
  current: FlaggedReason | null,
): FlaggedReason | null {
  const index = FLAG_CYCLE.indexOf(current);
  return FLAG_CYCLE[(index + 1) % FLAG_CYCLE.length] ?? null;
}

export function flaggedReasonLabel(reason: string): string {
  switch (reason) {
    case "ambiguous":
      return "ambiguous";
    case "low_quality":
      return "low quality";
    case "off_topic":
      return "off topic";
    default:
      return reason;
  }
}

export interface LabelDraft {
  flaggedReason: FlaggedReason | null;
  locator: Locator;
  value: boolean | null | string;
}

export interface InspectorLocalDraft {
  criterion: string;
  draft: LabelDraft;
  newOutput: MarkerOutput;
  displayName: string;
  seedKey: string;
  utteranceAnchor: number | null;
  validationError: string | null;
}

export const wholeTranscriptLocator: Locator = {
  kind: "whole_transcript",
};

function builtLocator(
  patch: Partial<typeof blankLocator> & Pick<typeof blankLocator, "kind">,
): Locator {
  const locator = buildLocator({ ...blankLocator, ...patch });
  if (typeof locator === "string") {
    throw new Error(locator);
  }
  return locator;
}

export function utteranceRangeLocator(
  firstIndex: number,
  secondIndex = firstIndex,
): Locator {
  const start = Math.min(firstIndex, secondIndex);
  const end = Math.max(firstIndex, secondIndex) + 1;
  return builtLocator({
    endIndexExclusive: String(end),
    kind: "utterance_range",
    startIndex: String(start),
  });
}

export function charSpanLocator(
  utteranceIndex: number,
  text: string,
  startCodeUnit: number,
  endCodeUnit: number,
): Locator {
  const start = Array.from(text.slice(0, startCodeUnit)).length;
  const end = Array.from(text.slice(0, endCodeUnit)).length;
  return builtLocator({
    endCharExclusive: String(end),
    kind: "char_span",
    startChar: String(start),
    utteranceIndex: String(utteranceIndex),
  });
}

export function traceNodeLocator(nodeId: string): Locator {
  return builtLocator({ kind: "trace_node", nodeId });
}

export function draftFromAnswer(answer: AnswerResponse): LabelDraft {
  return {
    flaggedReason: asFlaggedReason(answer.flagged_reason),
    locator: answer.locator ?? wholeTranscriptLocator,
    value: answerValueDraft(answer.value, answer.marker_output),
  };
}

export function blankLabelDraft(): LabelDraft {
  return {
    flaggedReason: null,
    locator: wholeTranscriptLocator,
    value: null,
  };
}

function asFlaggedReason(value: string | null): FlaggedReason | null {
  return value === "ambiguous" ||
    value === "low_quality" ||
    value === "off_topic"
    ? value
    : null;
}

function answerValueDraft(
  value: unknown,
  output: MarkerOutput,
): LabelDraft["value"] {
  if (output === "boolean") {
    return typeof value === "boolean" ? value : null;
  }
  if (output === "number") {
    return typeof value === "number" ? String(value) : "";
  }
  return typeof value === "string" ? value : "";
}

export function draftValue(
  draft: LabelDraft,
  output: MarkerOutput,
): boolean | number | string | null {
  if (output === "boolean") {
    return typeof draft.value === "boolean" ? draft.value : null;
  }
  if (typeof draft.value !== "string" || draft.value.trim().length === 0) {
    return null;
  }
  if (output === "category") {
    return draft.value.trim();
  }
  const value = Number(draft.value);
  return Number.isFinite(value) ? value : "Answer must be a number.";
}

export function draftEqualsAnswer(
  draft: LabelDraft,
  answer: AnswerResponse,
): boolean {
  const value = draftValue(draft, answer.marker_output);
  return (
    (typeof value !== "string" || answer.marker_output === "category") &&
    JSON.stringify(value) === JSON.stringify(answer.value) &&
    JSON.stringify(draft.locator) ===
      JSON.stringify(answer.locator ?? wholeTranscriptLocator) &&
    draft.flaggedReason === asFlaggedReason(answer.flagged_reason)
  );
}

export function saveButtonLabel(
  draft: LabelDraft,
  answer: AnswerResponse | null,
): "Confirm" | "Save correction" | "Save label" {
  if (answer == null) {
    return "Save label";
  }
  return draftEqualsAnswer(draft, answer) ? "Confirm" : "Save correction";
}

export function buildLabelBody(
  draft: LabelDraft,
  output: MarkerOutput,
  markerId: string,
  markerRevisionId: string,
  transcriptId: string,
  answer: AnswerResponse | null,
): LabelCreate | string {
  const value = draftValue(draft, output);
  if (typeof value === "string" && output === "number") {
    return value;
  }
  return {
    flagged_reason: draft.flaggedReason,
    locator: draft.locator,
    marker_id: markerId,
    marker_revision_id: markerRevisionId,
    source: "human",
    supersedes_id:
      answer != null && !draftEqualsAnswer(draft, answer) ? answer.id : null,
    transcript_id: transcriptId,
    value,
  };
}

export function assertionSuggestions(assertion: Assertion | null): string[] {
  const values = new Set<string>();
  for (const item of assertion?.cases ?? []) {
    const when = item.when;
    if ("equals" in when) {
      values.add(String(when.equals));
    } else if ("in" in when) {
      when.in.forEach((value) => values.add(value));
    } else if ("not_in" in when) {
      when.not_in.forEach((value) => values.add(value));
    } else {
      values.add(String(when.value));
    }
  }
  return [...values];
}

// The boolean a human writes to land on `verdict` under this assertion, or
// null when no boolean maps to it (the rail then offers no such button).
export function booleanForVerdict(
  assertion: Assertion | null,
  verdict: components["schemas"]["Verdict"],
): boolean | null {
  for (const candidate of [true, false]) {
    if (assertionVerdict(assertion, candidate) === verdict) {
      return candidate;
    }
  }
  return null;
}

export function assertionVerdict(
  assertion: Assertion | null,
  value: unknown,
): components["schemas"]["Verdict"] | null {
  if (assertion == null || value == null) {
    return null;
  }
  for (const item of assertion.cases) {
    if (conditionMatches(item.when, value)) {
      return item.then;
    }
  }
  return assertion.default;
}

function conditionMatches(
  condition: Assertion["cases"][number]["when"],
  value: unknown,
): boolean {
  if ("equals" in condition) {
    return value === condition.equals;
  }
  if ("in" in condition) {
    return typeof value === "string" && condition.in.includes(value);
  }
  if ("not_in" in condition) {
    return typeof value === "string" && !condition.not_in.includes(value);
  }
  if (typeof value !== "number") {
    return false;
  }
  switch (condition.operator) {
    case "==":
      return value === condition.value;
    case "!=":
      return value !== condition.value;
    case "<":
      return value < condition.value;
    case "<=":
      return value <= condition.value;
    case ">":
      return value > condition.value;
    case ">=":
      return value >= condition.value;
  }
}

export function locatorLabel(locator: Locator): string {
  switch (locator.kind) {
    case "whole_transcript":
      return "whole transcript";
    case "utterance_range":
      return `utterances ${String(locator.start_index + 1)}–${String(
        locator.end_index_exclusive,
      )}`;
    case "char_span":
      return `characters ${String(locator.start_char)}–${String(
        locator.end_char_exclusive,
      )} in utterance ${String((locator.utterance_index ?? 0) + 1)}`;
    case "audio_span":
      return `${formatSeconds(locator.start_ms)}–${formatSeconds(
        locator.end_ms_exclusive,
      )}`;
    case "trace_node":
      return `span ${locator.node_id}`;
  }
}

function formatSeconds(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${String(Math.floor(totalSeconds / 60))}:${seconds}`;
}
